import { useState } from 'react';
import { cn } from './cn';

/**
 * Salon photo with a tonal-tile fallback (design-spec §3 "Avatar fallback").
 *
 * Without a photo (or when it fails to load) it renders a solid tile with the
 * salon's first letter in Fraunces. The tile colour is deterministic per slug,
 * drawn from the design's muted tonal family (olive, plum, terracotta, khaki
 * + the gallery plums and the cognac accent option) so every salon keeps the
 * same colour on every page.
 *
 * Sizes (spec table):
 * - sm   44px, radius 12, initial 20  (past-booking rows)
 * - md   52px, radius 14, initial 24  (booking aside, confirmation)
 * - fill fills its parent (card headers, thumbnails, gallery); set the radius
 *        and initial size via `className` / `initialClassName`.
 *
 * Decorative by default (the salon name is printed next to it). Pass `label`
 * when the avatar stands alone.
 */

/** Tonal palette, from the board's avatar and gallery tiles. */
export const AVATAR_TONES = [
  '#5F6B3F', // olive
  '#7C4A64', // plum
  '#A0674D', // terracotta
  '#8A8355', // khaki
  '#6B3E56', // deep plum (gallery)
  '#8A5A44', // cognac (accent option)
] as const;

/** Stable 32-bit FNV-1a hash → index into AVATAR_TONES. */
export function avatarTone(slug: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < slug.length; i++) {
    h ^= slug.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return AVATAR_TONES[(h >>> 0) % AVATAR_TONES.length];
}

/** First letter of the name, uppercased; '·' when the name is blank. */
export function avatarInitial(name: string): string {
  const first = Array.from(name.trim())[0];
  return first ? first.toLocaleUpperCase() : '·';
}

export type SalonAvatarSize = 'sm' | 'md' | 'fill';

const sizeClasses: Record<SalonAvatarSize, { box: string; initial: string }> = {
  sm: { box: 'h-touch w-touch rounded-12', initial: 'text-[20px]' },
  md: { box: 'h-[52px] w-[52px] rounded-14', initial: 'text-[24px]' },
  fill: { box: 'h-full w-full', initial: 'text-[60px] opacity-50' },
};

export interface SalonAvatarProps {
  name: string;
  /** Salon subdomain/slug — drives the deterministic tone. Falls back to `name`. */
  slug?: string;
  imageUrl?: string | null;
  size?: SalonAvatarSize;
  /** Accessible name when the avatar isn't next to the salon name. */
  label?: string;
  className?: string;
  initialClassName?: string;
}

export function SalonAvatar({
  name,
  slug,
  imageUrl,
  size = 'md',
  label,
  className,
  initialClassName,
}: SalonAvatarProps) {
  const [failed, setFailed] = useState(false);
  const s = sizeClasses[size];
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': true as const };

  if (imageUrl && !failed) {
    return (
      <span
        {...a11y}
        className={cn(
          'block shrink-0 overflow-hidden bg-sl-map',
          s.box,
          className
        )}
      >
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      </span>
    );
  }

  return (
    <span
      {...a11y}
      data-tone={avatarTone(slug || name)}
      style={{ backgroundColor: avatarTone(slug || name) }}
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden',
        s.box,
        className
      )}
    >
      <span
        className={cn(
          'font-display font-normal leading-none text-sl-on-dark',
          s.initial,
          initialClassName
        )}
      >
        {avatarInitial(name)}
      </span>
    </span>
  );
}
